import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import {
  Loader2,
  LucideChevronDown,
  LucideDownload,
  LucideEllipsis,
  LucideEye,
  LucideX,
  Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import moment from 'moment';
import { Calendar } from '../UI/calendar';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from '../UI/alert-dialog';
import { toast } from 'sonner';
import POsBuilder from './POsBuilder';
import { determineStatusColor } from './helpers';
import { downloadPOPdf, getPOs, updatePO } from '../../api/pos';
import { valuteNumberFormatter } from '../../helper/numberFormatter';

type POsProps = {
  suppliers: any[];
  client: any;
  account: any;
  user: any;
  openLinkedPO: string;
  handleLinkedPOValue(val: string): void;
  handleTriggerProjectRefetch(): void;
};

export type BuilderSheetModeType = 'create' | 'update' | 'view';

export type POsRefType = {
  handleOpenPoBuilder(type: BuilderSheetModeType): void;
};

type BuilderSheetType = {
  active: boolean;
  disabled: boolean;
  mode: BuilderSheetModeType;
  row: any;
};

let initialBuilderState: BuilderSheetType = {
  active: false,
  disabled: false,
  mode: 'create',
  row: null,
};

const POs = forwardRef<POsRefType, POsProps>((props, ref) => {
  const { suppliers, client, account, user, openLinkedPO, handleLinkedPOValue, handleTriggerProjectRefetch } = props;

  const bodyWrapperRef = useRef<HTMLDivElement | null>(null);

  // *** STATES ***
  const [data, setData] = useState<any[]>([]);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [activeRow, setActiveRow] = useState<any>(null);
  const [builderSheet, setBuilderSheet] = useState<BuilderSheetType>(initialBuilderState);
  const [loading, setLoading] = useState<boolean>(true);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [downloadingPdfId, setDownloadingPdfId] = useState<number | null>(null);
  // *** END OF STATES ***

  const handleRefetch = () => {
    setRefetch((prev) => !prev);
  };

  const handleLoading = (val: boolean) => {
    setLoading(val);
  };

  const handleFetchPOs = async () => {
    try {
      handleLoading(true);
      const queryParams = {
        accountId: account?.id,
        clientId: client?.id,
      };

      const res = await getPOs(queryParams);

      setData(res?.data || []);
      handleLoading(false);
    } catch (error) {
      handleLoading(false);
      console.error(error);
    }
  };

  const handleShowDeleteModal = (val: boolean) => {
    setDeleteModal(val);
  };

  const handleSetActiveRow = (value: any) => {
    setActiveRow(value);
  };

  const openPdfResponse = (res: any, popup: Window | null) => {
    const downloadLink = res?.downloadLink || res?.pdfUrl;
    if (downloadLink) {
      if (popup) {
        popup.location.href = downloadLink;
      } else {
        window.open(downloadLink, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (res?.pdfData) {
      const binary = atob(res.pdfData);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }

      const blob = new Blob([bytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      if (popup) {
        popup.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    throw new Error('Invalid PDF response.');
  };

  const handleDownloadPDF = async (row: any) => {
    if (!row?.id || downloadingPdfId) return;

    let popup: Window | null = null;
    try {
      setDownloadingPdfId(row.id);
      // Keep a real window handle so we can reuse the same tab after async PDF generation.
      popup = window.open('about:blank', '_blank');
      if (popup) popup.opener = null;
      const res = await downloadPOPdf({
        id: row.id,
        accountId: row?.accountId || account?.id,
      });

      if (res?.error) {
        throw new Error(res?.message || 'Failed to generate PDF');
      }

      openPdfResponse(res, popup);
    } catch (error: any) {
      if (popup) popup.close();
      console.error(error);
      toast.error('Could not generate PDF');
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleDotsAction = (type: string, row?: any) => {
    switch (type) {
      case 'view':
        handleOpenPoBuilder('view', row);
        return;
      case 'download':
        handleDownloadPDF(row);
        return;
      case 'delete':
        handleSetActiveRow(row);
        handleShowDeleteModal(true);
        return;
      default:
        return;
    }
  };

  const handleDeleteAction = async (type: 'cancel' | 'delete') => {
    if (type === 'cancel') {
      handleSetActiveRow(null);
      handleShowDeleteModal(false);
    } else if (type === 'delete') {
      try {
        const payload = {
          id: activeRow?.id,
          deletedAt: new Date(),
          updatedBy: user?.id,
          actionType: 'po-table',
        };

        const res = await updatePO(payload);
        if (res) {
        }
        toast.success('PO deleted');
        handleRefetch();
      } catch (error) {
        console.error(error);
        toast.error('Failed to delete PO');
      } finally {
        handleShowDeleteModal(false);
        handleSetActiveRow(null);
      }
    }
  };

  const handleChooseStatus = async (item: any, value: string) => {
    setData((prev) => {
      return prev?.map((i: any) => {
        if (i?.id === item?.id) {
          return {
            ...i,
            status: value,
          };
        }
        return i;
      });
    });

    try {
      let payload = {
        id: item?.id,
        status: value,
        poNumber: item?.poNumber,
        updatedBy: user?.id,
        actionType: 'po-table',
        purchaseOrderBody: item?.purchaseOrderBody,
        poGenerated: ['canceled'].includes(value?.toLowerCase()) ? false : true,
      };

      if (value === 'Sent') {
        let now = new Date();
        payload['statusDate'] = now;

        if (!item?.sentDate) {
          payload['sentDate'] = now;
        }
        
        setData((prev) => {
          return prev.map((i: any) => {
            if (i?.id === item?.id) {
              return {
                ...i,
                statusDate: now,
                sentDate: i?.sentDate || now,
              };
            }
            return i;
          });
        });
      }

      const res = await updatePO(payload);

      if (res) {
          handleTriggerProjectRefetch()
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectDate = async (value: Date | undefined | null, item: any, key: string) => {
    setData((prev) => {
      return prev.map((i: any) => {
        if (i?.id === item?.id) {
          return {
            ...i,
            [key]: value,
          };
        }
        return i;
      });
    });

    try {
      const payload = {
        id: item?.id,
        [key]: value,
        updatedBy: user?.id,
        actionType: 'po-table',
        purchaseOrderBody: item?.purchaseOrderBody,
      };

      const res = await updatePO(payload);
      if (res) {
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenPoBuilder = (type: BuilderSheetModeType, row?: any) => {
    switch (type) {
      case 'create':
        setBuilderSheet({
          active: true,
          disabled: false,
          mode: type,
          row: row,
        });
        break;
      case 'update':
        setBuilderSheet({
          active: true,
          disabled: false,
          mode: type,
          row: row,
        });
        break;
      case 'view':
        setBuilderSheet({
          active: true,
          disabled: true,
          mode: type,
          row: row,
        });
        break;
      default:
        break;
    }
  };

  const handleBuilderSave = async () => {
    handleRefetch();
  };
  const handleBuilderSaveAndSend = async () => {};
  const handleBuilderEdit = async () => {
    setBuilderSheet((prev) => ({
      ...prev,
      disabled: false,
      mode: 'update',
    }));
  };
  const handleBuilderCancel = async (mode: BuilderSheetModeType = 'create') => {
    setBuilderSheet((prev) => (mode === 'view' ? { ...prev, mode } : initialBuilderState));
  };
  const handleBuilderSendPO = async () => {
    handleRefetch();
  };

  useEffect(() => {
    if (openLinkedPO) {
      let foundRowToOpen = data?.find((f: any) => f?.poNumber === openLinkedPO);

      if (foundRowToOpen) {
        handleOpenPoBuilder('view', foundRowToOpen);
      } else {
        const params = new URLSearchParams(window.location.search);
        params.delete('openPo');
        const newUrl = `${window.location.pathname}${params.toString() ? '?' + params : ''}`;
        history.replaceState(null, '', newUrl);
      }
    }
  }, [openLinkedPO, data]);

  useEffect(() => {
    if (!account?.id || !client?.id) return;

    handleFetchPOs();
  }, [account?.id, client?.id, refetch]);

  const poNextNumber = React.useMemo(() => {
    const todayKey = moment().format('YYYYMMDD');

    const maxForToday = (data || []).reduce((max: number, item: any) => {
      const poNumber = item?.poNumber;
      if (typeof poNumber !== 'string') return max;

      const match = poNumber.match(/^PO-(\d{8})-(\d{3})$/);
      if (!match) return max;

      const [, datePart, serialPart] = match;
      if (datePart !== todayKey) return max;

      const serial = Number(serialPart);
      if (Number.isNaN(serial)) return max;

      return Math.max(max, serial);
    }, 0);

    return String(maxForToday + 1).padStart(3, '0');
  }, [data]);

  // *** PROPS ***
  const posBuilderProps = {
    open: builderSheet?.active,
    mode: builderSheet?.mode,
    row: builderSheet?.row,
    footerActions: {
      save: handleBuilderSave,
      saveAndSend: handleBuilderSaveAndSend,
      edit: handleBuilderEdit,
      cancel: handleBuilderCancel,
      sendPO: handleBuilderSendPO,
    },
    suppliers: suppliers || [],
    client: client || null,
    account: account || null,
    user: user || null,
    poNextNumber,
    existingPOs: data || [],
    handleLinkedPOValue: handleLinkedPOValue,
    handleTriggerProjectRefetch: handleTriggerProjectRefetch,
  };
  // *** END PROPS ***

  // Pass to parent
  useImperativeHandle(ref, () => ({
    handleOpenPoBuilder,
  }));

  return (
    <div>
      <div className="w-full">
        <Table className="w-max min-w-full" wrapperRef={bodyWrapperRef}>
          <TableHeader>
            <TableRow>
              <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6">
                <div>
                  <span>PO</span>
                </div>
              </TableHead>
              <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6">
                <div>
                  <span>Supplier</span>
                </div>
              </TableHead>
              <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6">
                <div>
                  <span>Amount</span>
                </div>
              </TableHead>
              <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6">
                <div>
                  <span>Status</span>
                </div>
              </TableHead>
              <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6">
                <div>
                  <span>Created</span>
                </div>
              </TableHead>
              <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6">
                <div>
                  <span>Sent</span>
                </div>
              </TableHead>
              <TableHead className="h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6">
                <div>
                  <span>ETA</span>
                </div>
              </TableHead>
              <TableHead className="h-12 px-1.5 text-right align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 pr-6 last:w-[80px]">
                <div>
                  <span>Actions</span>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  <div className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading purchased orders...
                  </div>
                </TableCell>
              </TableRow>
            ) : !data || data?.length <= 0 ? (
              <TableRow className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
                <TableCell
                  colSpan={8}
                  className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-10 text-center text-muted-foreground text-sm"
                >
                  <span>No purchase orders saved for this project yet.</span>
                  <Button
                    className="p-0 ml-1 h-max min-h-0"
                    onClick={() => handleOpenPoBuilder('create')}
                    variant={'link'}
                  >
                    Create PO
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              (data || [])?.map((item: any) => {
                return (
                  <TableRow key={item?.id}>
                    <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 pr-6">
                      <div>
                        <Button
                          onClick={() => handleOpenPoBuilder('view', item)}
                          variant={'link'}
                          className="text-left underline-none p-0"
                        >
                          <span className="font-medium text-teal hover:underline wrap-text">
                            {item?.poNumber || '—'}
                          </span>
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-sm text-foreground pl-2">
                      <div>
                        <span className="wrap-text">{item?.supplier?.displayName || '—'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 font-medium">
                      <div>
                        <span className="wrap-text">
                          {valuteNumberFormatter(item?.orderTotal || 0)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 align-top">
                      <div className="flex flex-col gap-1 items-start">
                        <div className="inline-flex items-center gap-1.5">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                data-qa={`po_row_status_trigger_${item?.id}`}
                                variant={'text'}
                                className={cn(
                                  'rounded-2xl px-2.5 py-0.5 text-xs font-medium hover:opacity-80 transition-opacity min-h-0 max-h-[20px]',
                                  determineStatusColor(item?.status)
                                )}
                              >
                                <span className="wrap-text">{item?.status || 'select'}</span>
                                <LucideChevronDown className="lucide lucide-chevron-down h-3 w-3 shrink-0" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem
                                data-qa={`po_row_status_item_created_${item?.id}`}
                                className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                                onClick={() => handleChooseStatus(item, 'Created')}
                              >
                                <span className="inline-block h-2 w-2 rounded-full mr-2 bg-slate-100"></span>
                                <span>Created</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`po_row_status_item_sent_${item?.id}`}
                                className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                                onClick={() => handleChooseStatus(item, 'Sent')}
                              >
                                <span className="inline-block h-2 w-2 rounded-full mr-2 bg-blue-100"></span>
                                <span>Sent</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`po_row_status_item_acknowledg${item?.id}ed_`}
                                className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                                onClick={() => handleChooseStatus(item, 'Acknowledged')}
                              >
                                <span className="inline-block h-2 w-2 rounded-full mr-2 bg-indigo-100"></span>
                                <span>Acknowledged</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`po_row_status_item_partially_${item?.id}received_`}
                                className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                                onClick={() => handleChooseStatus(item, 'Partially Received')}
                              >
                                <span className="inline-block h-2 w-2 rounded-full mr-2 bg-amber-100"></span>
                                <span>Partially Received</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`po_row_status_item_received_${item?.id}`}
                                className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                                onClick={() => handleChooseStatus(item, 'Received')}
                              >
                                <span className="inline-block h-2 w-2 rounded-full mr-2 bg-green-100"></span>
                                <span>Received</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`po_row_status_item_closed_${item?.id}`}
                                className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                                onClick={() => handleChooseStatus(item, 'Closed')}
                              >
                                <span className="inline-block h-2 w-2 rounded-full mr-2 bg-emerald-100"></span>
                                <span>Closed</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                data-qa={`po_row_status_item_canceled_${item?.id}`}
                                className="relative flex select-none items-center rounded-sm px-2 py-1.5 outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-sm gap-0"
                                onClick={() => handleChooseStatus(item, 'Canceled')}
                              >
                                <span className="inline-block h-2 w-2 rounded-full mr-2 bg-red-100"></span>
                                <span>Canceled</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div
                          className={cn(
                            'inline-flex items-center gap-1 group text-xs',
                            item?.statusDate ? '[&:hover>.status-date-x]:opacity-100' : ''
                          )}
                        >
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                data-qa={`po_row_status_date_trigger_${item?.id}`}
                                type="button"
                                className={cn(
                                  'transition hover:text-primary text-left',
                                  !item?.statusDate ? 'text-muted-foreground' : ''
                                )}
                              >
                                {item?.statusDate
                                  ? moment(item?.statusDate).format('MM/DD/YY')
                                  : 'MM/DD/YY'}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-auto p-0">
                              <Calendar
                                data-qa={`po_row_status_date_calendar_${item?.id}`}
                                mode="single"
                                selected={item?.statusDate}
                                className="rounded-md border-0 p-[12px]"
                                onSelect={(v) => handleSelectDate(v, item, 'statusDate')}
                              />
                            </PopoverContent>
                          </Popover>

                          <Button
                            data-qa={`po_row_status_date_clear_${item?.id}`}
                            onClick={() => handleSelectDate(null, item, 'statusDate')}
                            className="status-date-x p-0 opacity-0 h-[unset]"
                            variant={'x_btn'}
                          >
                            <LucideX className="lucide lucide-x h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-xs text-muted-foreground">
                      <div>
                        <span className="wrap-text">
                          {item?.createdAt
                            ? moment(item?.createdAt).format('MM/DD/YY')
                            : 'MM/DD/YY'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-xs">
                      <div
                        className={cn(
                          'inline-flex items-center gap-1 group',
                          item?.sentDate ? '[&:hover>.sent-date-x]:opacity-100' : ''
                        )}
                      >
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              data-qa={`po_row_sent_date_trigger_${item?.id}`}
                              type="button"
                              className={cn(
                                'transition hover:text-primary text-left',
                                !item?.sentDate ? 'text-muted-foreground' : ''
                              )}
                            >
                              {item?.sentDate
                                ? moment(item?.sentDate).format('MM/DD/YY')
                                : 'MM/DD/YY'}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-auto p-0">
                            <Calendar
                              data-qa={`po_row_sent_date_calendar_${item?.id}`}
                              mode="single"
                              selected={item?.sentDate}
                              className="rounded-md border-0 p-[12px]"
                              onSelect={(v) => handleSelectDate(v, item, 'sentDate')}
                            />
                          </PopoverContent>
                        </Popover>

                        <Button
                          data-qa={`po_row_sent_date_clear_${item?.id}`}
                          onClick={() => handleSelectDate(null, item, 'sentDate')}
                          className="sent-date-x p-0 opacity-0 h-[unset]"
                          variant={'x_btn'}
                        >
                          <LucideX className="lucide lucide-x h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-xs">
                      <div
                        className={cn(
                          'inline-flex items-center gap-1 group',
                          item?.etaDate ? '[&:hover>.eta-date-x]:opacity-100' : ''
                        )}
                      >
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              data-qa={`po_row_eta_date_trigger_${item?.id}`}
                              type="button"
                              className={cn(
                                'transition hover:text-primary text-left',
                                !item?.etaDate ? 'text-muted-foreground' : ''
                              )}
                            >
                              {item?.etaDate
                                ? moment(item?.etaDate).format('MM/DD/YY')
                                : 'MM/DD/YY'}
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-auto p-0">
                            <Calendar
                              data-qa={`po_row_eta_date_calendar_${item?.id}`}
                              mode="single"
                              selected={item?.etaDate}
                              className="rounded-md border-0 p-[12px]"
                              onSelect={(v) => handleSelectDate(v, item, 'etaDate')}
                              disabled={{ before: new Date() }}
                            />
                          </PopoverContent>
                        </Popover>

                        <Button
                          data-qa={`po_row_eta_date_clear_${item?.id}`}
                          onClick={() => handleSelectDate(null, item, 'etaDate')}
                          className="eta-date-x p-0 opacity-0 h-[unset]"
                          variant={'x_btn'}
                        >
                          <LucideX className="lucide lucide-x h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 align-middle [&:has([role=checkbox])]:pr-0 first:pl-4 last:pr-4 py-3 text-right last:w-[80px]">
                      <div className="flex w-full justify-end">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <LucideEllipsis
                              data-qa={`po_row_dots_${item?.id}`}
                              className="cursor-pointer lucide lucide-ellipsis h-4 w-4"
                            />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className="z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 w-36"
                            align="end"
                          >
                            <DropdownMenuItem
                              data-qa={`po_row_dots_view_${item?.id}`}
                              onClick={() => handleDotsAction('view', item)}
                              className="gap-0 relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer"
                            >
                              <LucideEye className="lucide lucide-eye mr-2 h-3.5 w-3.5" />
                              <span>View</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`po_row_dots_download_${item?.id}`}
                              onClick={() => handleDotsAction('download', item)}
                              disabled={downloadingPdfId === item?.id}
                              className="gap-0 relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer"
                            >
                              {downloadingPdfId === item?.id ? (
                                <Loader2 className="lucide lucide-loader-2 mr-2 h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <LucideDownload className="lucide lucide-download mr-2 h-3.5 w-3.5" />
                              )}
                              <span>
                                {downloadingPdfId === item?.id ? 'Generating...' : 'Download'}
                              </span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-qa={`po_row_dots_delete_${item?.id}`}
                              onClick={() => handleDotsAction('delete', item)}
                              className="gap-0 relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-destructive hover:text-destructive cursor-pointer"
                            >
                              <Trash2 className="text-destructive lucide lucide-trash2 mr-2 h-3.5 w-3.5" />
                              <span className="text-destructive">Delete</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <POsBuilder {...posBuilderProps} />

      <AlertDialog open={deleteModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <h2 className="m-0 text-lg font-semibold">Delete this PO?</h2>
            <p className="text-sm text-muted-foreground">
              This will permanently remove the purchase order from this project's library. This
              cannot be undone.
            </p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              data-qa={'alert_modal_cancel'}
              onClick={() => handleDeleteAction('cancel')}
              variant={'outline'}
              className="font-medium"
            >
              Cancel
            </Button>
            <Button
              data-qa={'alert_modal_confirm'}
              onClick={() => handleDeleteAction('delete')}
              variant={'delete'}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});

export default POs;
